// eslint-disable-next-line no-unused-vars -- 3.4.2026
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useProCalculator } from '../hooks/useProCalculator';
import SectionHelp from './SectionHelp';
import ToggleSwitch from './ToggleSwitch';
import { lengthToDisplay, lengthFromDisplay, weightToDisplay, weightFromDisplay, lengthUnitLabel, weightUnitLabel, areaUnitLabel, densityUnitLabel, areaToDisplay, densityToDisplay } from '../utils/unitConversion';
import { lengthUnitWarning, weightUnitWarning } from '../utils/unitWarnings';
import { getReferenceStatus } from '../utils/sectionCompletionUtils';
import { useAuth } from '../contexts/AuthContext';
import ProCalculatorFooter from './ProCalculatorFooter';
import { Save, Pencil, RectangleHorizontal, Square, Circle } from 'lucide-react';
import '../styles/ReferenceSection.css';

const SHOW_SIGNUP_NUDGE = true;

const ReferenceSection = () => {
  const { user } = useAuth();

  const {
    reference,
    defaultReference,
    useDefaultReference,
    setUseDefaultReference,
    handleReferenceChange,
    isMetric,
    setIsMetric,
    isMetricWeight,
    setIsMetricWeight,
    isMobile,
    goToNextSection,
    activeSection,
    loadedPlanName,
    showAdvanced,
    updateAdvancedFeatures,
  } = useProCalculator();

  // Unit-mismatch warnings: set on blur, cleared on any keystroke
  const [warnings, setWarnings] = useState({});
  const clearWarning = (field) => setWarnings(prev => {
    if (!prev[field]) return prev;
    const next = { ...prev }; delete next[field]; return next;
  });
  const evalWarning = (field, value, type) => {
    const w = type === 'length' ? lengthUnitWarning(value, isMetric) : weightUnitWarning(value, isMetricWeight);
    setWarnings(prev => w ? { ...prev, [field]: w } : (({ [field]: _, ...rest }) => rest)(prev));
  };
  
  // Populate fields with default values when useDefaultReference is true and fields are empty
  useEffect(() => {
    if (useDefaultReference) {
      const isEmpty = !reference.width && !reference.length && !reference.weightNoIcing && !reference.weight;
      
      if (isEmpty) {
        handleReferenceChange('width', defaultReference.width);
        handleReferenceChange('length', defaultReference.length);
        handleReferenceChange('weightNoIcing', defaultReference.weightNoIcing);
        handleReferenceChange('weight', defaultReference.weight);
      }
    }
  }, [useDefaultReference, reference.width, reference.length, reference.weightNoIcing, reference.weight, handleReferenceChange, defaultReference]);
  
  // Determine if the section is complete
  const referenceStatus = getReferenceStatus(reference, showAdvanced);
  const isReferenceSectionComplete = referenceStatus === 'completed';

  // Shape type for the reference cookie (local to Advanced mode, stored in reference state)
  const shapeType = reference.shapeType || 'rectangle';

  // Calculate derived values using useMemo
  const calculatedValues = useMemo(() => {
    const width = parseFloat(reference.width);
    const length = parseFloat(reference.length);
    const diameter = parseFloat(reference.diameter);
    const manualArea = parseFloat(reference.manualArea);
    const weightNoIcing = parseFloat(reference.weightNoIcing);
    const weight = parseFloat(reference.weight);
    const shape = reference.shapeType || 'rectangle';

    let icingWeight = NaN;
    let area = NaN;
    let icingPerArea = NaN;

    if (!isNaN(weight) && !isNaN(weightNoIcing) && weight >= weightNoIcing) {
      icingWeight = weight - weightNoIcing;
    }

    if (shape === 'rectangle' && !isNaN(width) && !isNaN(length) && width > 0 && length > 0) {
      area = width * length;
    } else if (shape === 'square' && !isNaN(width) && width > 0) {
      area = width * width;
    } else if (shape === 'circle' && !isNaN(diameter) && diameter > 0) {
      const r = diameter / 2;
      area = Math.PI * r * r;
    } else if (shape === 'manual' && !isNaN(manualArea) && manualArea > 0) {
      area = manualArea;
    }

    if (!isNaN(icingWeight) && !isNaN(area) && area > 0) {
      icingPerArea = icingWeight / area;
    }

    return { icingWeight, area, icingPerArea };
  }, [reference.width, reference.length, reference.diameter, reference.manualArea, reference.weightNoIcing, reference.weight, reference.shapeType]);

  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const { icingWeight, area, icingPerArea } = calculatedValues;

  // Handle default reference toggle
  const handleDefaultReferenceToggle = (isUsingDefault) => {
    setUseDefaultReference(isUsingDefault);
    
    // Whether toggling ON or OFF, seed the reference fields with the live admin defaults.
    // Toggling ON: fields are hidden but the value drives calculations.
    // Toggling OFF: user starts with the admin defaults pre-filled (not blank).
    Object.entries(defaultReference).forEach(([key, value]) => {
      handleReferenceChange(key, value);
    });
  };
  
  return (
    <>
      {/* Sign-up nudge — only shown to guests */}
      {SHOW_SIGNUP_NUDGE && !user && (
        <div className="pro-signup-nudge">
          <span className="pro-signup-nudge-icon"><Save size={22} /></span>
          <div className="pro-signup-nudge-text">
            <strong>Want to save your plan, export to PDF / Excel, or use the AI tools?</strong>{' '}
            Create a free account. It only takes a moment, and your work here won't be lost.
          </div>
          <a href="/login?tab=signup&redirect=/pro" className="pro-signup-nudge-btn">Sign Up Free</a>
        </div>
      )}

      {/* Pro Calculator overview — orients first-time visitors before the step-by-step boxes start */}
      <div className="pro-overview-box">
        <h2 className="pro-overview-title">Welcome to the Pro Calculator</h2>
        <SectionHelp sectionId="proOverview" isMobile={isMobile} />
      </div>

      {/* Get Started intro box — sits above everything */}
      <div className="get-started-intro-box">
        <div className="section-header">
          <h2 className="section-title">Let's Customize It</h2>
          {loadedPlanName && <span className="loaded-plan-name">Plan: {loadedPlanName}</span>}
        </div>
        <p className="reference-subtitle">
          Choose your preferred measurement units below. You can change these at any time.
        </p>

      </div>

      {/* Unit preference box */}
      <div className="unit-prefs-box">
        <h3 className="unit-prefs-title">Measurement Units</h3>
        <p className="unit-prefs-desc">Choose whether you measure in inches or millimeters, and ounces or grams.</p>
        <div className="unit-toggles">
          <div className="unit-toggle-group">
            <label className="reference-toggle-label">inches</label>
            <ToggleSwitch isOn={isMetric} onToggle={(val) => setIsMetric(val)} />
            <label className="reference-toggle-label">mm</label>
          </div>
          <div className="unit-toggle-group">
            <label className="reference-toggle-label">oz</label>
            <ToggleSwitch isOn={isMetricWeight} onToggle={(val) => setIsMetricWeight(val)} />
            <label className="reference-toggle-label">grams</label>
          </div>
        </div>

        {user && (
          <>
            <h3 className="unit-prefs-title unit-prefs-title--precision">Calculation Detail</h3>
            <p className="unit-prefs-desc">Simple uses average icing density for a quick estimate. Advanced lets you enter your own cookie's measurements for a more tailored result.</p>
            <div className="unit-toggles">
              <div className="unit-toggle-group unit-toggle-group--advanced">
                <label className="reference-toggle-label">Simple</label>
                <ToggleSwitch isOn={showAdvanced} onToggle={(val) => updateAdvancedFeatures(val)} />
                <label className="reference-toggle-label advanced-label">
                  <span className="advanced-label-full">Advanced</span>
                  <span className="advanced-label-short">Adv</span>
                </label>
              </div>
            </div>
          </>
        )}

        {!user && (
          <p className="unit-prefs-desc unit-prefs-signin-hint">
            <a href="/login?tab=signup&redirect=/pro">Sign in free</a> to unlock Advanced mode and AI-powered tools.
          </p>
        )}
      </div>

      {showAdvanced && <div className="reference-section">

        <p className="reference-quick-start-hint">
          In Advanced mode you can supply your own test cookie measurements so the calculator uses your own icing thickness. Not sure which to pick? The default is a well-tested average that works great for most cookies — just leave it as is.
        </p>

        {/* Default vs. custom density toggle — moved above the rest of the group so it can gate everything below it */}
        <div className="ref-density-mode-row">
          <span className="ref-density-mode-label">Use default thickness</span>
          <ToggleSwitch
            isOn={reference.useDefaultDensity === false}
            onToggle={(val) => handleReferenceChange('useDefaultDensity', !val)}
          />
          <span className="ref-density-mode-label">Enter my own</span>
        </div>

        {/* Rest of the group box only matters once the user opts into a custom density */}
        {reference.useDefaultDensity === false && <>

        <SectionHelp sectionId="reference" isMobile={isMobile} />

        {/* Custom density form */}
        <div className="reference-form">

          {/* ── Group 1: Shape & Dimensions ── */}
          <div className="ref-group">
            {/* Shape selector */}
            <div className="reference-field-row">
              <div className="reference-field reference-field--shape">
                <label>Cookie Shape</label>
                <div className="ref-shape-options">
                  {['rectangle', 'square', 'circle', 'manual'].map(s => (
                    <button
                      key={s}
                      type="button"
                      className={`ref-shape-btn${shapeType === s ? ' active' : ''}`}
                      onClick={() => handleReferenceChange('shapeType', s)}
                    >
                      {s === 'rectangle' && <><RectangleHorizontal size={16} />Rectangle</>}
                      {s === 'square'    && <><Square size={16} />Square</>}
                      {s === 'circle'    && <><Circle size={16} />Circle</>}
                      {s === 'manual'    && <><Pencil size={16} />Enter Area</>}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Dimension inputs — driven by shape */}
            <div className="reference-field-row input-row">
              {shapeType === 'rectangle' && (
                <>
                  <div className="reference-field">
                    <label htmlFor="pro-ref-width">Width: <span className="unit-label">({lengthUnitLabel(isMetric)})</span></label>
                    <input
                      id="pro-ref-width" type="number"
                      value={lengthToDisplay(reference.width, isMetric)}
                      onChange={(e) => { clearWarning('width'); handleReferenceChange('width', lengthFromDisplay(e.target.value, isMetric)); }}
                      onBlur={(e) => evalWarning('width', e.target.value, 'length')}
                      className="reference-input" min="0" step="0.01"
                    />
                    {warnings.width && <div className="unit-warning">{warnings.width}</div>}
                  </div>
                  <div className="reference-field">
                    <label htmlFor="pro-ref-length">Length: <span className="unit-label">({lengthUnitLabel(isMetric)})</span></label>
                    <input
                      id="pro-ref-length" type="number"
                      value={lengthToDisplay(reference.length, isMetric)}
                      onChange={(e) => { clearWarning('length'); handleReferenceChange('length', lengthFromDisplay(e.target.value, isMetric)); }}
                      onBlur={(e) => evalWarning('length', e.target.value, 'length')}
                      className="reference-input" min="0" step="0.01"
                    />
                    {warnings.length && <div className="unit-warning">{warnings.length}</div>}
                  </div>
                </>
              )}
              {shapeType === 'square' && (
                <div className="reference-field">
                  <label htmlFor="pro-ref-width">Side Length: <span className="unit-label">({lengthUnitLabel(isMetric)})</span></label>
                  <input
                    id="pro-ref-width" type="number"
                    value={lengthToDisplay(reference.width, isMetric)}
                    onChange={(e) => { clearWarning('width'); handleReferenceChange('width', lengthFromDisplay(e.target.value, isMetric)); }}
                    onBlur={(e) => evalWarning('width', e.target.value, 'length')}
                    className="reference-input" min="0" step="0.01"
                  />
                  {warnings.width && <div className="unit-warning">{warnings.width}</div>}
                </div>
              )}
              {shapeType === 'circle' && (
                <div className="reference-field">
                  <label htmlFor="pro-ref-diameter">Diameter: <span className="unit-label">({lengthUnitLabel(isMetric)})</span></label>
                  <input
                    id="pro-ref-diameter" type="number"
                    value={lengthToDisplay(reference.diameter || '', isMetric)}
                    onChange={(e) => { clearWarning('diameter'); handleReferenceChange('diameter', lengthFromDisplay(e.target.value, isMetric)); }}
                    onBlur={(e) => evalWarning('diameter', e.target.value, 'length')}
                    className="reference-input" min="0" step="0.01"
                  />
                  {warnings.diameter && <div className="unit-warning">{warnings.diameter}</div>}
                </div>
              )}

              {/* Cookie Area — editable for manual, display-only for other shapes */}
              <div className="reference-field calculated-field">
                <label htmlFor="pro-ref-area">
                  Cookie Area: <span className="unit-label">({areaUnitLabel(isMetric)})</span>
                </label>
                {shapeType === 'manual' ? (
                  <input
                    id="pro-ref-area" type="number"
                    value={reference.manualArea !== '' && reference.manualArea !== undefined ? areaToDisplay(reference.manualArea, isMetric) : ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '' || raw === null) { handleReferenceChange('manualArea', ''); return; }
                      const displayVal = parseFloat(raw);
                      if (isNaN(displayVal)) return;
                      const CM_PER_INCH = 2.54;
                      const sqInches = isMetric ? displayVal / (CM_PER_INCH * CM_PER_INCH) : displayVal;
                      handleReferenceChange('manualArea', sqInches);
                    }}
                    className="reference-input" min="0" step="0.01"
                  />
                ) : (
                  <input
                    id="pro-ref-area" type="text"
                    value={!isNaN(area) ? areaToDisplay(area, isMetric).toFixed(3) : '-'}
                    readOnly disabled
                    className="calculated-value-input"
                  />
                )}
              </div>
            </div>
          </div>

          {/* ── Divider ── */}
          <div className="ref-group-divider" />

          {/* ── Group 2: Weight ── */}
          <div className="ref-group">
            <div className="reference-field-row input-row">
              <div className="reference-field">
                <label htmlFor="pro-ref-weight-no-icing">Weight without Icing: <span className="weight-unit">({weightUnitLabel(isMetricWeight)})</span></label>
                <input
                  id="pro-ref-weight-no-icing" type="number"
                  value={weightToDisplay(reference.weightNoIcing, isMetricWeight)}
                  onChange={(e) => { clearWarning('weightNoIcing'); handleReferenceChange('weightNoIcing', weightFromDisplay(e.target.value, isMetricWeight)); }}
                  onBlur={(e) => evalWarning('weightNoIcing', e.target.value, 'weight')}
                  className="reference-input" min="0" step="0.01"
                />
                {warnings.weightNoIcing && <div className="unit-warning">{warnings.weightNoIcing}</div>}
              </div>
              <div className="reference-field">
                <label htmlFor="pro-ref-weight">Weight with Icing: <span className="weight-unit">({weightUnitLabel(isMetricWeight)})</span></label>
                <input
                  id="pro-ref-weight" type="number"
                  value={weightToDisplay(reference.weight, isMetricWeight)}
                  onChange={(e) => { clearWarning('weight'); handleReferenceChange('weight', weightFromDisplay(e.target.value, isMetricWeight)); }}
                  onBlur={(e) => evalWarning('weight', e.target.value, 'weight')}
                  className="reference-input" min="0" step="0.01"
                />
                {warnings.weight && <div className="unit-warning">{warnings.weight}</div>}
              </div>
              <div className="reference-field calculated-field">
                <label htmlFor="pro-ref-icing-used">Icing Used: <span className="weight-unit">({weightUnitLabel(isMetricWeight)})</span></label>
                <input
                  id="pro-ref-icing-used" type="text"
                  value={!isNaN(icingWeight) ? weightToDisplay(icingWeight, isMetricWeight).toFixed(2) : '-'}
                  readOnly disabled className="calculated-value-input"
                />
              </div>
              <div className="reference-field calculated-field">
                <label htmlFor="pro-ref-icing-per-area">Icing Density: <span className="weight-unit">({densityUnitLabel(isMetric, isMetricWeight)})</span></label>
                <input
                  id="pro-ref-icing-per-area" type="text"
                  value={!isNaN(icingPerArea) ? densityToDisplay(icingPerArea, isMetric, isMetricWeight).toFixed(4) : '-'}
                  readOnly disabled className="calculated-value-input"
                />
              </div>
            </div>
          </div>

        </div>

        </>}

      </div>}
      {activeSection !== 'all' && (

        <ProCalculatorFooter
          onNext={() => goToNextSection('reference')}
          nextLabel="Next: Cookie Names"
          nextDisabled={!isReferenceSectionComplete}
        />
      )}
    </>
  );
};

export default ReferenceSection;
